import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  confirmPasswordReset,
  ConfirmPasswordResetError,
} from './confirmPasswordResetApi';
import ResetPasswordScreen, {
  type ResetPasswordScreenMode,
} from './ResetPasswordScreen';

const LINKTREE_BASE_URL =
  import.meta.env.VITE_LINKTREE_BASE_URL || 'https://linktr.ee';

type Translate = (key: string) => string;

interface ResetPasswordFlowProps {
  confirmationToken?: string;
}

const getScreenFromErrorCode = (
  error: ConfirmPasswordResetError
): ResetPasswordScreenMode => {
  switch (error.code) {
    case 'PASSWORD_RESET_TOKEN_EXPIRED':
      return 'expired';
    case 'PASSWORD_RESET_TOKEN_INVALID':
    case 'PASSWORD_RESET_USER_NOT_FOUND':
      return 'invalid';
    default:
      return 'technicalError';
  }
};

const getFormErrorFromErrorCode = (
  error: ConfirmPasswordResetError,
  t: Translate
): string | undefined => {
  switch (error.code) {
    case 'PASSWORD_RESET_CURRENT_PASSWORD':
      return t("Choose a password you haven't used before.");
    case 'PASSWORD_INPUT_INVALID':
      return error.message && error.message !== error.code
        ? error.message
        : t('Enter a valid password.');
    default:
      return undefined;
  }
};

const ResetPasswordFlow: React.FC<ResetPasswordFlowProps> = ({
  confirmationToken,
}) => {
  const { t } = useTranslation();
  const [screen, setScreen] = useState<ResetPasswordScreenMode>(
    confirmationToken ? 'form' : 'invalid'
  );
  const [serverErrorText, setServerErrorText] = useState<string | undefined>();

  const handleSubmit = async (password: string) => {
    if (!confirmationToken) {
      setScreen('invalid');
      return;
    }

    try {
      await confirmPasswordReset({
        baseUrl: LINKTREE_BASE_URL,
        confirmationToken,
        password,
      });
      setScreen('success');
    } catch (error) {
      if (error instanceof ConfirmPasswordResetError) {
        const formError = getFormErrorFromErrorCode(error, t);

        if (formError) {
          setServerErrorText(formError);
          setScreen('form');
          return;
        }

        setScreen(getScreenFromErrorCode(error));
        return;
      }

      setScreen('technicalError');
    }
  };

  return (
    <ResetPasswordScreen
      screen={screen}
      serverErrorText={serverErrorText}
      onSubmit={handleSubmit}
      onRequestNewLink={() => {
        window.location.href = `${LINKTREE_BASE_URL}/request`;
      }}
      onBackToLogin={() => {
        window.location.href = `${LINKTREE_BASE_URL}/login`;
      }}
      resetError={() => setServerErrorText(undefined)}
    />
  );
};

export default ResetPasswordFlow;
